import { ApplicationScope } from "../types/App";

type ApiDirectoryEntry = (entityId?: string) => string;

export type ApiDirectoryEntryName =
  | "getMyUser"
  | "getMyContact"
  | "setMyMFA"
  | "setMyAIConsent"
  | "getContact"
  | "clearContactCaches"
  | "setUserMFA"
  | "getKeycloakUserInfo"
  | "setTemporaryPassword"
  | "setUserEnabled"
  | "getMyBankAccount"
  | "createMyBankAccount"
  | "getMyEmployerPayrollCycles"
  | "getMyEmployerDossiers"
  | "getEmployerDossier"
  | "getEmployerDossierTermination"
  | "getEmployerDossierTerminationStatus"
  | "getEmployerDossierTerminationInvoicePDF"
  | "getEmployerDossierTerminationPayrollAdvicePDF"
  | "terminateEmployerDossier"
  | "getMyEmployerVehicleSettlementSurveyResponses"
  | "getMyEmployerSummaryStats"
  | "getMyEmployerActiveLeaseChartData"
  | "getMyEmployerPayrollTaxSavingsChartData"
  | "getMyEmployerVehicleSettlementSurveyResponseChartData"
  | "getMyEmployerDossierSavingsSummaries"
  | "qualifyLead"
  | "qualifyNewLead"
  | "qualifyNewLeadForContact"
  | "getMyDossiers"
  | "getMyDossiersForEmployerApproval"
  | "getDossiersForContact"
  | "approveDriver"
  | "rejectDriver"
  | "getEmployerDocuments"
  | "getEmployerDocumentPDF"
  | "getDossier"
  | "getDossierByOpportunityId"
  | "terminateDossier"
  | "setDriverNotifiedOfTermination"
  | "getDossierTermination"
  | "getDossierTerminationStatus"
  | "getDossierStatus"
  | "getEmptyDossierStatus"
  | "getDossierQuotes"
  | "getDossierQuotesWithVehicleSpecifications"
  | "getDossierVehicleComparisons"
  | "createDossierQuoteComparisonView"
  | "patchDossierQuoteComparisonView"
  | "createDossierVehicleComparisonView"
  | "patchDossierVehicleComparisonView"
  | "createDossierVehicleSearchEvent"
  | "patchDossierVehicleSearchEvent"
  | "setPrimaryQuote"
  | "getFinanceApplication"
  | "updateFinanceApplication"
  | "updateFinanceApplicationImages"
  | "getAssetsAndLiabilitiesTypes"
  | "getSpousalDeclarationPDF"
  | "getDirectDebitTermsPDF"
  | "createDossierBankAccount"
  | "getPackagingAgreementPDF"
  | "getEmployeePackagingAgreementPDF"
  | "requestPackagingAgreement"
  | "signPackagingAgreement"
  | "requestEmployerAccountSetup"
  | "getListQuote"
  | "getQuote"
  | "getQuoteVehicleImages"
  | "getQuoteVehicleInfo"
  | "getQuoteOptions"
  | "getQuotePDF"
  | "getQuoteVehicleSuggestions"
  | "getQuoteFinanceComparison"
  | "createQuoteFinanceComparisonView"
  | "patchQuoteFinanceComparisonView"
  | "getQuoteCalculations"
  | "getQuoteDealerPricingRequests"
  | "getQuoteDealerPricingRequestCounts"
  | "createDealerPricingRequests"
  | "selectDealerPricingRequest"
  | "searchDealerPricingRequests"
  | "getDealerPricingRequestAggregations"
  | "getQuoteFinanceComparisonCalculations"
  | "getQuoteInclusionCalculations"
  | "createQuote"
  | "updateQuote"
  | "deleteQuote"
  | "setQuoteVisibility"
  | "setQuoteLocked"
  | "setQuoteCalculationStatusOverride"
  | "getSyncQuotesStatus"
  | "syncQuotes"
  | "getVehicleMakes"
  | "getVehicleModels"
  | "getVehicleModelYears"
  | "getVehicleVariants"
  | "searchVehicleVariants"
  | "getVehicleVariant"
  | "getVehicleVariantExists"
  | "getVehicleOptions"
  | "getVehicleImages"
  | "getVehicleAspects"
  | "getVehicleDealers"
  | "getFile"
  | "getApiVersion"
  | "getFaqAppLocations"
  | "getFaqs"
  | "getFaq"
  | "getFaqVote"
  | "createFaqVote"
  | "createFaqView"
  | "getSurvey"
  | "hasSurveyAwaitingResponse"
  | "updateSurveyResponse"
  | "getPublicConfig"
  | "getProductTours"
  | "createProductTourView"
  | "setProductTourView"
  | "getMySalesOwner"
  | "messageMySalesOwner"
  | "messageDriverSupport"
  | "messageCorporateSupport"
  | "requestQuote"
  | "getAwaitingExpenseClaims"
  | "getResolvedExpenseClaims"
  | "getExpenseClaimFile"
  | "createExpenseClaim"
  | "cancelExpenseClaim"
  | "extractReceiptData"
  | "getDossierTransactions"
  | "getDossierBudgetTransactions"
  | "getDossierTransactionComparison"
  | "getSyncDossierTransactionsStatus"
  | "syncDossierTransactions"
  | "getRepairDossierTransactionsStatus"
  | "repairDossierTransactions"
  | "getDossierInvoiceComparison"
  | "getDossierChartData"
  | "getDossierDocuments"
  | "getDossierDocumentPDF"
  | "getDossierVehicleReview"
  | "chooseDossierVehicleSourcingOption"
  | "requestDossierVehiclePricing"
  | "confirmWillSourceVehicle";

export type ApiDirectory = Record<
  ApiDirectoryEntryName,
  ApiDirectoryEntry | undefined
>;

type ApiRouteMap = {
  internal?: ApiDirectoryEntry;
  external?: ApiDirectoryEntry;
  shared?: ApiDirectoryEntry;
};

const apiRoutes: Record<ApiDirectoryEntryName, ApiRouteMap> = {
  getMyUser: {
    internal: () => "/internal/users/me",
    external: () => "/external/driver/users/me",
  },
  getMyContact: {
    external: () => "/external/driver/users/me/contact",
  },
  setMyMFA: {
    external: () => "/external/driver/users/me/set-mfa",
  },
  setMyAIConsent: {
    external: () => "/external/driver/users/me/ai-consent",
  },
  getContact: {
    internal: (contactId?: string) => `/internal/users/${contactId}`,
  },
  getMySalesOwner: {
    external: () => "/external/driver/users/me/sales-owner",
  },
  clearContactCaches: {
    internal: (contactId?: string) =>
      `/internal/users/${contactId}/clear-caches`,
  },
  setUserMFA: {
    internal: (contactId?: string) => `/internal/users/${contactId}/set-mfa`,
  },
  getKeycloakUserInfo: {
    internal: (contactId?: string) =>
      `/internal/users/${contactId}/keycloak-info`,
  },
  setTemporaryPassword: {
    internal: (contactId?: string) =>
      `/internal/users/${contactId}/set-temp-password`,
  },
  setUserEnabled: {
    internal: (contactId?: string) =>
      `/internal/users/${contactId}/set-enabled`,
  },
  getMyBankAccount: {
    external: () => "/external/driver/users/me/direct-debit",
  },
  createMyBankAccount: {
    external: () => "/external/driver/users/me/direct-debit",
  },
  getMyEmployerPayrollCycles: {
    external: () => "/external/employer/users/me/employer-payroll-cycles",
  },
  getMyEmployerDossiers: {
    external: () => "/external/employer/dossier-actions/list",
  },
  getEmployerDossier: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}`,
  },
  getEmployerDossierTermination: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}/termination`,
  },
  getEmployerDossierTerminationStatus: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}/termination-status`,
  },
  getEmployerDossierTerminationInvoicePDF: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}/termination-invoice-pdf`,
  },
  getEmployerDossierTerminationPayrollAdvicePDF: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}/termination-payroll-advice-pdf`,
  },
  terminateEmployerDossier: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}/terminate`,
  },
  setDriverNotifiedOfTermination: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}/set-driver-notified-of-termination`,
  },
  getMyEmployerVehicleSettlementSurveyResponses: {
    external: () =>
      "/external/employer/survey-actions/vehicle-settlement-responses",
  },
  getMyEmployerSummaryStats: {
    external: () => "/external/employer/stats-actions/employer-summary-stats",
  },
  getMyEmployerActiveLeaseChartData: {
    external: () => "/external/employer/stats-actions/active-lease-chart-data",
  },
  getMyEmployerPayrollTaxSavingsChartData: {
    external: () =>
      "/external/employer/stats-actions/payroll-tax-savings-chart-data",
  },
  getMyEmployerVehicleSettlementSurveyResponseChartData: {
    external: () =>
      "/external/employer/stats-actions/vehicle-settlement-survey-response-chart-data",
  },
  getMyEmployerDossierSavingsSummaries: {
    external: () => "/external/employer/dossier-actions/savings-summaries",
  },
  approveDriver: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}/approve-driver`,
  },
  rejectDriver: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}/reject-driver`,
  },
  getEmployerDocuments: {
    external: () => "/external/employer/document-actions/list",
  },
  getEmployerDocumentPDF: {
    external: (documentId?: string) =>
      `/external/employer/documents/${documentId}/pdf`,
  },
  getMyDossiers: {
    external: () => "/external/driver/dossier-actions/list",
  },
  getMyDossiersForEmployerApproval: {
    external: () => "/external/employer/dossier-actions/for-approval",
  },
  getDossiersForContact: {
    internal: (contactId?: string) => `/internal/users/${contactId}/dossiers`,
  },
  qualifyLead: {
    external: () => "/external/driver/users/qualify-lead",
  },
  qualifyNewLead: {
    external: () => "/external/driver/users/qualify-new-lead",
  },
  qualifyNewLeadForContact: {
    internal: (contactId?: string) =>
      `/internal/users/${contactId}/qualify-new-lead`,
  },
  getDossier: {
    internal: (dossierId?: string) => `/internal/dossiers/${dossierId}`,
    external: (dossierId?: string) => `/external/driver/dossiers/${dossierId}`,
  },
  getDossierByOpportunityId: {
    internal: (opporuntiyId?: string) =>
      `/internal/dossier-actions/id-from-opportunity/${opporuntiyId}`,
  },
  terminateDossier: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/terminate`,
  },
  getDossierTermination: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/termination`,
  },
  getDossierTerminationStatus: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/status`,
  },
  getDossierStatus: {
    internal: (dossierId?: string) => `/internal/dossiers/${dossierId}/status`,
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/status`,
  },
  getEmptyDossierStatus: {
    shared: () => "/shared/dossiers/empty-status",
  },
  getDossierQuotes: {
    internal: (dossierId?: string) => `/internal/dossiers/${dossierId}/quotes`,
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/quotes`,
  },
  getDossierQuotesWithVehicleSpecifications: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/quotes-with-specs`,
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/quotes-with-specs`,
  },
  getDossierVehicleComparisons: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/vehicle-comparisons`,
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/vehicle-comparisons`,
  },
  createDossierQuoteComparisonView: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/quote-comparison-view`,
  },
  patchDossierQuoteComparisonView: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/quote-comparison-view`,
  },
  createDossierVehicleComparisonView: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/vehicle-comparison-view`,
  },
  patchDossierVehicleComparisonView: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/vehicle-comparison-view`,
  },
  createDossierVehicleSearchEvent: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/vehicle-search-event`,
  },
  patchDossierVehicleSearchEvent: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/vehicle-search-event`,
  },
  setPrimaryQuote: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/set-primary-quote`,
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/set-primary-quote`,
  },
  getFinanceApplication: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/finance-application`,
  },
  updateFinanceApplication: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/finance-application`,
  },
  updateFinanceApplicationImages: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/finance-application/images`,
  },
  getAssetsAndLiabilitiesTypes: {
    shared: () => "/shared/dossiers/assets-and-liability-types",
  },
  getSpousalDeclarationPDF: {
    shared: () => "/shared/dossiers/spousal-declaration-pdf",
  },
  getDirectDebitTermsPDF: {
    shared: () => "/shared/dossiers/direct-debit-terms-pdf",
  },
  createDossierBankAccount: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/direct-debit`,
  },
  getPackagingAgreementPDF: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/packaging-agreement-pdf`,
  },
  getEmployeePackagingAgreementPDF: {
    external: (dossierId?: string) =>
      `/external/employer/dossiers/${dossierId}/packaging-agreement-pdf`,
  },
  requestPackagingAgreement: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/request-packaging-agreement`,
  },
  signPackagingAgreement: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/sign-packaging-agreement`,
  },
  requestEmployerAccountSetup: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/request-account-setup`,
  },
  getListQuote: {
    internal: (quoteId?: string) => `/internal/quotes/${quoteId}`,
    external: (quoteId?: string) => `/external/driver/quotes/${quoteId}`,
  },
  getQuote: {
    internal: (quoteId?: string) => `/internal/quotes/${quoteId}/calculated`,
    external: (quoteId?: string) =>
      `/external/driver/quotes/${quoteId}/calculated`,
  },
  getQuoteVehicleImages: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/vehicle-images`,
    external: (quoteId?: string) =>
      `/external/driver/quotes/${quoteId}/vehicle-images`,
  },
  getQuoteVehicleInfo: {
    internal: (quoteId?: string) => `/internal/quotes/${quoteId}/vehicle-info`,
    external: (quoteId?: string) =>
      `/external/driver/quotes/${quoteId}/vehicle-info`,
  },
  getQuoteOptions: {
    internal: (quoteId?: string) => `/internal/quotes/${quoteId}/options`,
    external: (quoteId?: string) =>
      `/external/driver/quotes/${quoteId}/options`,
  },
  getQuotePDF: {
    internal: (quoteId?: string) => `/internal/quotes/${quoteId}/pdf`,
    external: (quoteId?: string) => `/external/driver/quotes/${quoteId}/pdf`,
  },
  getQuoteVehicleSuggestions: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/vehicle-suggestions`,
    external: (quoteId?: string) =>
      `/external/driver/quotes/${quoteId}/vehicle-suggestions`,
  },
  getQuoteFinanceComparison: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/finance-comparison`,
    external: (quoteId?: string) =>
      `/external/driver/quotes/${quoteId}/finance-comparison`,
  },
  createQuoteFinanceComparisonView: {
    external: (quoteId?: string) =>
      `/external/driver/quotes/${quoteId}/finance-comparison-view`,
  },
  patchQuoteFinanceComparisonView: {
    external: (quoteId?: string) =>
      `/external/driver/quotes/${quoteId}/finance-comparison-view`,
  },
  getQuoteCalculations: {
    internal: (quoteId?: string) => `/internal/quotes/${quoteId}/calculations`,
  },
  getQuoteDealerPricingRequests: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/dealer-pricing-requests`,
  },
  getQuoteDealerPricingRequestCounts: {
    external: (quoteId?: string) =>
      `/external/driver/quotes/${quoteId}/dealer-pricing-request-counts`,
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/dealer-pricing-request-counts`,
  },
  createDealerPricingRequests: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/dealer-pricing-requests`,
  },
  selectDealerPricingRequest: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/select-dealer-pricing-request`,
  },
  searchDealerPricingRequests: {
    internal: () => "/internal/quote-actions/dealer-pricing-request-search",
  },
  getDealerPricingRequestAggregations: {
    internal: () =>
      "/internal/quote-actions/dealer-pricing-request-aggregations",
  },
  getQuoteFinanceComparisonCalculations: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/finance-comparison-calculations`,
  },
  getQuoteInclusionCalculations: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/inclusion-calculations`,
  },
  getSyncQuotesStatus: {
    internal: (contactId?: string) =>
      `/internal/users/${contactId}/sync-quotes/status`,
  },
  syncQuotes: {
    internal: (contactId?: string) =>
      `/internal/users/${contactId}/sync-quotes`,
  },
  createQuote: {
    internal: (dossierId?: string) => `/internal/dossiers/${dossierId}/quotes`,
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/quotes`,
  },
  updateQuote: {
    internal: (quoteId?: string) => `/internal/quotes/${quoteId}`,
    external: (quoteId?: string) => `/external/driver/quotes/${quoteId}`,
  },
  deleteQuote: {
    internal: (quoteId?: string) => `/internal/quotes/${quoteId}`,
    external: (quoteId?: string) => `/external/driver/quotes/${quoteId}`,
  },
  setQuoteVisibility: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/set-visibility`,
  },
  setQuoteLocked: {
    internal: (quoteId?: string) => `/internal/quotes/${quoteId}/set-locked`,
  },
  setQuoteCalculationStatusOverride: {
    internal: (quoteId?: string) =>
      `/internal/quotes/${quoteId}/set-calculation-status-override`,
  },
  getVehicleMakes: {
    shared: () => "/shared/vehicles/makes",
  },
  getVehicleModels: {
    shared: () => "/shared/vehicles/models",
  },
  getVehicleModelYears: {
    shared: () => "/shared/vehicles/model-years",
  },
  getVehicleVariants: {
    shared: () => "/shared/vehicles/variants",
  },
  searchVehicleVariants: {
    shared: () => "/shared/vehicles/variants/search",
  },
  getVehicleAspects: {
    shared: () => "/shared/vehicles/aspects",
  },
  getVehicleDealers: {
    internal: () => "/internal/quote-actions/vehicle-dealers",
  },
  getVehicleVariant: {
    shared: (variantCode?: string) =>
      `/shared/vehicles/variants/${variantCode}`,
  },
  getVehicleVariantExists: {
    shared: (variantCode?: string) =>
      `/shared/vehicles/variants/${variantCode}/exists`,
  },
  getVehicleOptions: {
    shared: (variantId?: string) =>
      `/shared/vehicles/variants/${variantId}/options`,
  },
  getVehicleImages: {
    shared: (variantCode?: string) =>
      `/shared/vehicles/variants/${variantCode}/images`,
  },
  getFile: {
    shared: (filePath?: string) => `${filePath}`,
  },
  getApiVersion: {
    shared: () => "/shared/app/api-version",
  },
  getFaqAppLocations: {
    shared: (pageId?: string) =>
      `/shared/app/pages/${pageId}/faq-app-locations`,
  },
  getFaqs: {
    shared: (appLocationId?: string) =>
      `/shared/app/app-locations/${appLocationId}/faqs`,
  },
  getFaq: {
    shared: (faqId?: string) => `/shared/faqs/${faqId}`,
  },
  getFaqVote: {
    external: (faqId?: string) => `/external/driver/faqs/${faqId}/vote`,
  },
  createFaqVote: {
    external: (faqId?: string) => `/external/driver/faqs/${faqId}/vote`,
  },
  createFaqView: {
    external: (faqId?: string) => `/external/driver/faqs/${faqId}/view`,
  },
  getSurvey: {
    external: (surveyId?: string) => `/external/driver/surveys/${surveyId}`,
  },
  hasSurveyAwaitingResponse: {
    external: (surveyId?: string) =>
      `/external/driver/surveys/${surveyId}/awaiting-response`,
  },
  updateSurveyResponse: {
    external: (surveyId?: string) =>
      `/external/driver/surveys/${surveyId}/response`,
  },
  getPublicConfig: {
    shared: () => "/shared/configurations/public",
  },
  getProductTours: {
    external: (pageId?: string) =>
      `/external/driver/product-tour-actions/page/${pageId}`,
  },
  createProductTourView: {
    external: (productTourId?: string) =>
      `/external/driver/product-tours/${productTourId}/view`,
  },
  setProductTourView: {
    external: (productTourId?: string) =>
      `/external/driver/product-tours/${productTourId}/view`,
  },
  messageMySalesOwner: {
    external: () => "/external/driver/users/me/message-sales-owner",
  },
  messageDriverSupport: {
    external: () => "/external/driver/users/me/message-driver-support",
  },
  messageCorporateSupport: {
    external: () => "/external/employer/users/me/message-corporate-support",
  },
  requestQuote: {
    external: () => "/external/driver/users/me/request-quote",
  },
  getAwaitingExpenseClaims: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/awaiting-expense-claims`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/awaiting-expense-claims`,
  },
  getResolvedExpenseClaims: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/resolved-expense-claims`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/resolved-expense-claims`,
  },
  createExpenseClaim: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/expense-claim`,
  },
  cancelExpenseClaim: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/cancel-expense-claim`,
  },
  extractReceiptData: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/extract-receipt-data`,
  },
  getExpenseClaimFile: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/expense-claim-file`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/expense-claim-file`,
  },
  getDossierTransactions: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/transactions`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/transactions`,
  },
  getDossierBudgetTransactions: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/budget-transactions`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/budget-transactions`,
  },
  getDossierTransactionComparison: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/transaction-comparison`,
  },
  getSyncDossierTransactionsStatus: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/sync-transactions/status`,
  },
  syncDossierTransactions: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/sync-transactions`,
  },
  getRepairDossierTransactionsStatus: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/repair-transactions/status`,
  },
  repairDossierTransactions: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/repair-transactions`,
  },
  getDossierInvoiceComparison: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/invoice-comparison`,
  },
  getDossierChartData: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/chart-data`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/chart-data`,
  },
  getDossierDocuments: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/documents`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/documents`,
  },
  getDossierDocumentPDF: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/document-pdf`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/document-pdf`,
  },
  getDossierVehicleReview: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/vehicle-review`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/vehicle-review`,
  },
  chooseDossierVehicleSourcingOption: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/vehicle-sourcing-option`,
  },
  requestDossierVehiclePricing: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/request-vehicle-pricing`,
  },
  confirmWillSourceVehicle: {
    external: (dossierId?: string) =>
      `/external/driver/dossiers/${dossierId}/confirm-will-source-vehicle`,
  },
};

function getRouteByType(
  entryName: ApiDirectoryEntryName,
  applicationScope: ApplicationScope
) {
  return (
    apiRoutes[entryName][applicationScope] || apiRoutes[entryName]["shared"]
  );
}

export const buildApiDirectory = (
  applicationType: ApplicationScope
): ApiDirectory => {
  return (Object.keys(apiRoutes) as ApiDirectoryEntryName[]).reduce(
    (directory, entryName: ApiDirectoryEntryName) => {
      directory[entryName] = getRouteByType(entryName, applicationType);
      return directory;
    },
    {} as ApiDirectory
  );
};
